import { NormalizedDropdownItemType } from '@entur/dropdown';
import { getPartnerLabel } from 'features/partners/utils';
import { DatasetSubscriptionDtoResponse, DatasetSubscriptionInputType } from 'features/report-templates/types';
import {
  transactionTypesIncomingSettlementURL,
  transactionTypesSettlementURL,
} from 'features/transaction-types/constants';
import { DatasetSubscriptionDto, DropdownRptV2, DropdownRptV2WithId, PageAndSortParamsType, PartnerRptV2 } from 'types';

interface DropdownItemLabelOptions {
  useDescription?: boolean;
  showStatus?: boolean;
}

/**
 * Generates a display label for a dropdown item.
 *
 * @param item - The dropdown item to generate a label for.
 * @param options - Options to customize the label:
 *   - useDescription: Use the item's description instead of its name.
 *   - showStatus: Append a status indicator (🟡 or 🔴) based on the item's status.
 * @returns The formatted label string for the dropdown item.
 */
export const getDropdownItemLabel = (item?: DropdownRptV2, options: DropdownItemLabelOptions = {}): string => {
  if (!item || !item.id) return 'Ukjent';

  const { useDescription = false, showStatus = false } = options;
  const { id, name, description } = item;

  const label = useDescription && description ? description : `${name ?? 'Ukjent'} (${id})`;

  if (item.status === 2 && showStatus) {
    return `${label} 🟡`;
  }
  if (item.status === 3 && showStatus) {
    return `${label} 🔴`;
  }

  return label;
};

// This function filters out:
// - Items without an id
// - Items with status 3 (discontinued) unless they are selected
const filterDropdownItems = (
  items: (Omit<DropdownRptV2, 'id'> & { id?: string | number })[],
  selectedIds?: number[]
): DropdownRptV2WithId[] => {
  return items.filter(
    (item) => Boolean(item.id) && (item.status !== 3 || selectedIds?.includes(Number(item.id)))
  ) as DropdownRptV2WithId[];
};

/**
 * Normalizes and filters dropdown items for use in dropdown components.
 *
 * - Filters out items without an id.
 * - Filters out discontinued items (status 3) unless they are currently selected.
 * - Ensures any selected items not present in the items list are still included.
 * - Maps items to the normalized dropdown item format with label and value.
 *
 * @param items - The list of dropdown items to process.
 * @param selectedIds - The currently selected id(s) to ensure inclusion.
 * @param options - Options for label formatting:
 *   - useDescription: Use the item's description instead of its name.
 *   - showStatus: Append a status indicator (🟡 or 🔴) based on the item's status.
 * @returns An array of normalized dropdown items with label and value.
 */
export const getDropdownItems = (
  items?: (Omit<DropdownRptV2, 'id'> & { id?: string | number })[],
  selectedIds?: number[] | number | null,
  options: DropdownItemLabelOptions = {}
): NormalizedDropdownItemType<number>[] => {
  if (!items) return [];

  const selectedIdsArray = Array.isArray(selectedIds) ? selectedIds : selectedIds ? [selectedIds] : undefined;

  const filteredItems = filterDropdownItems(items, selectedIdsArray);

  // Find all unknown items and add them to the list
  const missingIds = selectedIdsArray?.filter((id) => !filteredItems.some((item) => Number(item.id) === id)) ?? [];
  const missingItems = missingIds.map((id) => ({ id: String(id) })) || [];
  const allItems = [...filteredItems, ...missingItems];

  return allItems.map((item) => ({
    label: getDropdownItemLabel(item, options),
    value: Number(item.id),
  }));
};

export const omitPageSort = <T extends PageAndSortParamsType>(values: T) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { page, size, sort, direction, ...rest } = values;
  return rest;
};

// A factory, not a shared constant - every caller needs its own row (and its own
// `partnerCopies` array) so editing one form can't mutate another form's defaults.
export const createEmptyDatasetSubscriptionRow = (): DatasetSubscriptionInputType => ({
  reportTemplateId: null,
  partnerCopies: [],
  fromAccountingMonthId: null,
  toAccountingMonthId: undefined,
  status: 1,
});

export const formatDatasetSubscriptionsForFrontend = (
  datasetSubscriptions: DatasetSubscriptionDtoResponse[],
  reportTemplates: DropdownRptV2[],
  partners: PartnerRptV2[]
): DatasetSubscriptionInputType[] => {
  const hasDatasetSubscriptions = datasetSubscriptions && datasetSubscriptions.length > 0;

  // Add empty row if it is undefined/empty
  if (!hasDatasetSubscriptions) {
    return [createEmptyDatasetSubscriptionRow()];
  }

  // Sort partnerCopies within each subscription by partner label
  const subscriptionsWithSortedPartnerCopies = datasetSubscriptions.map((subscription) => {
    const sortedPartnerCopies = [...subscription.partnerCopies].sort((a, b) => {
      const labelA = getPartnerLabel(partners.find((partner) => partner.id === a));
      const labelB = getPartnerLabel(partners.find((partner) => partner.id === b));
      return labelA.localeCompare(labelB);
    });
    // The backend represents "ongoing" (no end date) as either a missing field or an
    // explicit null; normalize both to undefined here so the rest of the app only ever
    // has to handle one shape for "unset".
    return {
      ...subscription,
      partnerCopies: sortedPartnerCopies,
      toAccountingMonthId: subscription.toAccountingMonthId ?? undefined,
    };
  });

  // Sort subscriptions alphabetically by report template name
  const subscriptionsSorted = [...subscriptionsWithSortedPartnerCopies].sort((a, b) => {
    const templateA = reportTemplates.find((template) => template.id === a.reportTemplateId.toString());
    const templateB = reportTemplates.find((template) => template.id === b.reportTemplateId.toString());
    return (templateA?.name ?? '').localeCompare(templateB?.name ?? '');
  });

  return subscriptionsSorted;
};

export const formatDatasetSubscriptionsForBackend = (
  datasetSubscriptions: DatasetSubscriptionInputType[]
): DatasetSubscriptionDto[] => {
  // Only a row with both required ids actually filled in is valid to submit - narrowing via a
  // type predicate (rather than filtering loosely and then `Number(...)`-coercing) means a row
  // that's still missing one of them is dropped instead of silently becoming `Number(null) = 0`.
  const isCompleteRow = (
    row: DatasetSubscriptionInputType
  ): row is DatasetSubscriptionInputType & { reportTemplateId: number; fromAccountingMonthId: number } =>
    row.reportTemplateId != null && row.fromAccountingMonthId != null && row.partnerCopies.length > 0;

  // Explicit null and "field absent" both mean "ongoing" to the backend, but only the latter
  // survives JSON.stringify's key-dropping - normalize here too so the outgoing payload never
  // sends an explicit null, regardless of what ended up in form state upstream.
  return datasetSubscriptions
    .filter(isCompleteRow)
    .map((row) => ({ ...row, toAccountingMonthId: row.toAccountingMonthId ?? undefined }));
};

export const getTransactionTypeURL = (annexType: string, incoming: boolean): string => {
  const urlMap = incoming ? transactionTypesIncomingSettlementURL : transactionTypesSettlementURL;
  return urlMap[annexType as keyof typeof urlMap] || urlMap.GENERIC_TRANSACTION;
};
